import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CalendarDays, Plus, Search, Filter, Phone, Mail, Car, MapPin, Clock,
  Mic, MicOff, Send, Truck, User, X, CheckCircle, XCircle, Loader2,
  ChevronDown, Calendar as CalendarIcon, ArrowRight, Edit3, RefreshCw, Share2, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Voice to Text Hook
const useVoiceToText = (onResult) => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-IN';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        onResult(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
        toast.error('Voice recognition failed');
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [onResult]);

  const startListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.start();
      setIsListening(true);
    } else {
      toast.error('Voice recognition not supported');
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  return { isListening, startListening, stopListening };
};

// Voice Input Component
const VoiceInput = ({ value, onChange, placeholder, label, required, multiline, className }) => {
  const { theme } = useTheme();
  const inputRef = useRef(null);
  
  const handleVoiceResult = useCallback((transcript) => {
    onChange(value ? `${value} ${transcript}` : transcript);
  }, [value, onChange]);

  const { isListening, startListening, stopListening } = useVoiceToText(handleVoiceResult);

  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className="space-y-1">
      {label && <Label className={theme.text}>{label} {required && <span className="text-red-500">*</span>}</Label>}
      <div className="relative">
        <InputComponent
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`pr-10 ${className} ${theme.inputBg} ${theme.border}`}
          required={required}
        />
        <button
          type="button"
          onClick={isListening ? stopListening : startListening}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-colors ${
            isListening ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-600 hover:bg-slate-500 text-slate-300'
          }`}
          title={isListening ? 'Stop recording' : 'Start voice input'}
        >
          {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

// Create Booking Modal
const CreateBookingModal = ({ open, onClose, garageId, onBookingCreated, brands, models, fuelTypes }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [drivers, setDrivers] = useState([]);
  
  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_number: '',
    vehicle_fuel_type: '',
    service_category: '',
    preferred_date: '',
    preferred_time: '',
    address: '',
    customer_complaint: '',
    requires_pickup: false,
    customer_id: null,
    vehicle_id: null
  });

  const services = [
    'General Service', 'Denting & Painting', 'AC Service & Repair',
    'Polishing', 'Interior Detailing', 'Ceramic Coating', 'Other Repairs'
  ];

  const allTimeSlots = [
    '9:00 AM - 11:00 AM', '11:00 AM - 1:00 PM', '1:00 PM - 3:00 PM',
    '3:00 PM - 5:00 PM', '5:00 PM - 7:00 PM'
  ];

  // Get available time slots - requires 2 hour window for same-day bookings
  const getAvailableTimeSlots = () => {
    if (!selectedDate) return allTimeSlots;
    
    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    
    if (!isToday) return allTimeSlots;
    
    // For same-day bookings, require at least 2 hours from now
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeIn24 = currentHour + (currentMinute / 60);
    const minBookingTime = currentTimeIn24 + 2; // 2-hour minimum window
    
    return allTimeSlots.filter(slot => {
      // Parse slot start time: "9:00 AM - 11:00 AM" -> extract "9:00 AM"
      const startTime = slot.split(' - ')[0].trim();
      const [time, period] = startTime.split(' ');
      const [hourStr, minuteStr] = time.split(':');
      let hour = parseInt(hourStr);
      
      // Convert to 24-hour format
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
      
      const slotStartTime = hour + (parseInt(minuteStr || '0') / 60);
      
      return slotStartTime >= minBookingTime;
    });
  };

  const availableTimeSlots = getAvailableTimeSlots();

  useEffect(() => {
    if (open && garageId) {
      fetchDrivers();
    }
  }, [open, garageId]);

  const fetchDrivers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/garage/bookings/drivers/available?garage_id=${garageId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('garage_token')}` }
      });
      const data = await res.json();
      if (data.success) setDrivers(data.drivers);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const searchCustomer = async (phone) => {
    if (phone.length < 10) return;
    
    setSearchingCustomer(true);
    try {
      const res = await fetch(`${API_URL}/api/garage/bookings/customer/search?phone=${phone}&garage_id=${garageId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('garage_token')}` }
      });
      const data = await res.json();
      
      if (data.found) {
        setCustomerFound(true);
        setCustomerVehicles(data.vehicles);
        setForm(prev => ({
          ...prev,
          customer_name: data.customer.name,
          customer_email: data.customer.email || '',
          customer_id: data.customer.id,
          address: data.customer.address || ''
        }));
        toast.success('Customer found!');
      } else {
        setCustomerFound(false);
        setCustomerVehicles([]);
      }
    } catch (error) {
      console.error('Error searching customer:', error);
    } finally {
      setSearchingCustomer(false);
    }
  };

  const handleVehicleSelect = (vehicleId) => {
    const vehicle = customerVehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      setForm(prev => ({
        ...prev,
        vehicle_id: vehicleId,
        vehicle_brand: vehicle.brand,
        vehicle_model: vehicle.model,
        vehicle_number: vehicle.vehicle_number,
        vehicle_fuel_type: vehicle.fuel_type
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.customer_name || !form.customer_phone || !form.vehicle_brand || 
        !form.service_category || !form.preferred_date || !form.preferred_time) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/garage/bookings?garage_id=${garageId}&source=manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('garage_token')}`
        },
        body: JSON.stringify(form)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Booking created successfully!');
        onBookingCreated(data.booking);
        onClose();
        resetForm();
      } else {
        toast.error(data.detail || 'Failed to create booking');
      }
    } catch (error) {
      toast.error('Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      customer_name: '', customer_phone: '', customer_email: '',
      vehicle_brand: '', vehicle_model: '', vehicle_number: '',
      vehicle_fuel_type: '', service_category: '', preferred_date: '',
      preferred_time: '', address: '', customer_complaint: '',
      requires_pickup: false, customer_id: null, vehicle_id: null
    });
    setCustomerFound(false);
    setCustomerVehicles([]);
    setSelectedDate(null);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${theme.cardBg} ${theme.border}`}>
        <DialogHeader>
          <DialogTitle className={`${theme.text} flex items-center gap-2`}>
            <CalendarDays className="w-5 h-5 text-orange-500" />
            Create New Booking
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer Details */}
          <div className={`p-4 rounded-lg ${theme.cardBgSolid} border ${theme.border}`}>
            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${theme.text}`}>
              <User className="w-4 h-4 text-orange-500" /> Customer Details
            </h3>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <VoiceInput
                  label="Phone Number"
                  value={form.customer_phone}
                  onChange={(val) => {
                    const phone = val.replace(/\D/g, '').slice(0, 10);
                    setForm(prev => ({ ...prev, customer_phone: phone }));
                    if (phone.length === 10) searchCustomer(phone);
                  }}
                  placeholder="9876543210"
                  required
                />
                {searchingCustomer && <p className="text-xs text-orange-500 mt-1">Searching...</p>}
                {customerFound && <p className="text-xs text-green-500 mt-1">✓ Existing customer</p>}
              </div>
              
              <VoiceInput
                label="Name"
                value={form.customer_name}
                onChange={(val) => setForm(prev => ({ ...prev, customer_name: val }))}
                placeholder="Customer name"
                required
              />
              
              <VoiceInput
                label="Email"
                value={form.customer_email}
                onChange={(val) => setForm(prev => ({ ...prev, customer_email: val }))}
                placeholder="email@example.com"
                className="col-span-2"
              />
            </div>
          </div>

          {/* Vehicle Details */}
          <div className={`p-4 rounded-lg ${theme.cardBgSolid} border ${theme.border}`}>
            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${theme.text}`}>
              <Car className="w-4 h-4 text-orange-500" /> Vehicle Details
            </h3>
            
            {customerVehicles.length > 0 && (
              <div className="mb-3">
                <Label className={theme.text}>Select Existing Vehicle</Label>
                <Select onValueChange={handleVehicleSelect}>
                  <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                    <SelectValue placeholder="Choose a vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {customerVehicles.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Or enter new vehicle details below</p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={theme.text}>Brand *</Label>
                <Select value={form.vehicle_brand} onValueChange={(val) => setForm(prev => ({ ...prev, vehicle_brand: val, vehicle_model: '' }))}>
                  <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className={theme.text}>Model</Label>
                <Select value={form.vehicle_model} onValueChange={(val) => setForm(prev => ({ ...prev, vehicle_model: val }))}>
                  <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {(models[form.vehicle_brand] || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <VoiceInput
                label="Vehicle Number"
                value={form.vehicle_number}
                onChange={(val) => setForm(prev => ({ ...prev, vehicle_number: val.toUpperCase() }))}
                placeholder="KA01AB1234"
                required
              />
              
              <div>
                <Label className={theme.text}>Fuel Type *</Label>
                <Select value={form.vehicle_fuel_type} onValueChange={(val) => setForm(prev => ({ ...prev, vehicle_fuel_type: val }))}>
                  <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                    <SelectValue placeholder="Select fuel" />
                  </SelectTrigger>
                  <SelectContent>
                    {fuelTypes.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Service & Schedule */}
          <div className={`p-4 rounded-lg ${theme.cardBgSolid} border ${theme.border}`}>
            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${theme.text}`}>
              <Clock className="w-4 h-4 text-orange-500" /> Service & Schedule
            </h3>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className={theme.text}>Service Type *</Label>
                <Select value={form.service_category} onValueChange={(val) => setForm(prev => ({ ...prev, service_category: val }))}>
                  <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className={theme.text}>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={`w-full justify-start text-left ${theme.inputBg} ${theme.border}`}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setForm(prev => ({ ...prev, preferred_date: format(date, 'yyyy-MM-dd'), preferred_time: '' }));
                      }}
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <Label className={theme.text}>Time Slot *</Label>
                <Select value={form.preferred_time} onValueChange={(val) => setForm(prev => ({ ...prev, preferred_time: val }))}>
                  <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTimeSlots.length > 0 ? (
                      availableTimeSlots.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)
                    ) : (
                      <SelectItem value="none" disabled>No slots available today</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {selectedDate && selectedDate.toDateString() === new Date().toDateString() && availableTimeSlots.length === 0 && (
                  <p className="text-xs text-orange-500 mt-1">Same-day bookings require 2 hours notice. Please select tomorrow.</p>
                )}
              </div>
            </div>
          </div>

          {/* Pickup & Notes */}
          <div className={`p-4 rounded-lg ${theme.cardBgSolid} border ${theme.border}`}>
            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${theme.text}`}>
              <Truck className="w-4 h-4 text-orange-500" /> Pickup & Notes
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requires_pickup"
                  checked={form.requires_pickup}
                  onChange={(e) => setForm(prev => ({ ...prev, requires_pickup: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="requires_pickup" className={theme.text}>Requires Pickup</Label>
              </div>
              
              {form.requires_pickup && (
                <VoiceInput
                  label="Pickup Address"
                  value={form.address}
                  onChange={(val) => setForm(prev => ({ ...prev, address: val }))}
                  placeholder="Enter pickup address"
                  multiline
                />
              )}
              
              <VoiceInput
                label="Customer Complaint / Notes"
                value={form.customer_complaint}
                onChange={(val) => setForm(prev => ({ ...prev, customer_complaint: val }))}
                placeholder="Describe the issue..."
                multiline
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className={theme.border}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-orange-500 hover:bg-orange-600">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Booking
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Bookings Component
const Bookings = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [garageId, setGarageId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, tomorrow, later
  const [bucketFilter, setBucketFilter] = useState('active'); // active, successful, missed
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState({});
  const [fuelTypes] = useState(['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid']);

  // Helper function to check if booking date is today
  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const bookingDate = new Date(dateStr);
    return bookingDate.toDateString() === today.toDateString();
  };

  // Helper function to check if booking date is tomorrow
  const isTomorrow = (dateStr) => {
    if (!dateStr) return false;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const bookingDate = new Date(dateStr);
    return bookingDate.toDateString() === tomorrow.toDateString();
  };

  // Helper function to check if booking date is in the future (after tomorrow)
  const isLater = (dateStr) => {
    if (!dateStr) return false;
    const dayAfterTomorrow = new Date();
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    dayAfterTomorrow.setHours(0, 0, 0, 0);
    const bookingDate = new Date(dateStr);
    return bookingDate >= dayAfterTomorrow;
  };

  // Helper function to check if booking date/time has passed
  const isPastBooking = (dateStr, timeStr) => {
    if (!dateStr) return false;
    const now = new Date();
    const bookingDate = new Date(dateStr);
    
    // If booking date is in the past
    if (bookingDate.toDateString() < now.toDateString()) return true;
    
    // If same day, check if time has passed
    if (bookingDate.toDateString() === now.toDateString() && timeStr) {
      // Parse time like "3:00 PM - 5:00 PM" to get end time
      const endTime = timeStr.split(' - ')[1];
      if (endTime) {
        const [time, period] = endTime.trim().split(' ');
        const [hourStr, minuteStr] = time.split(':');
        let hour = parseInt(hourStr);
        if (period === 'PM' && hour !== 12) hour += 12;
        if (period === 'AM' && hour === 12) hour = 0;
        
        const bookingEndTime = new Date(bookingDate);
        bookingEndTime.setHours(hour, parseInt(minuteStr || '0'), 0, 0);
        
        return now > bookingEndTime;
      }
    }
    return false;
  };

  // Categorize bookings into buckets
  const categorizeBookings = (bookingsList) => {
    const active = [];
    const successful = [];
    const missed = [];

    bookingsList.forEach(booking => {
      if (booking.job_id) {
        // Has job card created = Successful
        successful.push(booking);
      } else if (isPastBooking(booking.preferred_date, booking.preferred_time)) {
        // No job card and date/time passed = Missed
        missed.push(booking);
      } else {
        // No job card but date hasn't passed = Active
        active.push(booking);
      }
    });

    return { active, successful, missed };
  };

  useEffect(() => {
    const token = localStorage.getItem('garage_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setGarageId(payload.garage_id);
      } catch (e) {
        console.error('Invalid token');
      }
    }
  }, []);

  useEffect(() => {
    if (garageId) {
      fetchBookings();
      fetchDrivers();
      fetchVehicleData();
    }
  }, [garageId, statusFilter]);

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams({ garage_id: garageId });
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await fetch(`${API_URL}/api/garage/bookings?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('garage_token')}` }
      });
      const data = await res.json();
      if (data.success) setBookings(data.bookings);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/garage/bookings/drivers/available?garage_id=${garageId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('garage_token')}` }
      });
      const data = await res.json();
      if (data.success) setDrivers(data.drivers);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchVehicleData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/garage-booking/pricing/vehicles-with-categories`);
      const data = await res.json();
      if (data.success && data.vehicles) {
        // Extract unique brands
        const uniqueBrands = [...new Set(data.vehicles.map(v => v.brand))];
        setBrands(uniqueBrands);
        
        // Group models by brand
        const modelsByBrand = {};
        data.vehicles.forEach(v => {
          if (!modelsByBrand[v.brand]) {
            modelsByBrand[v.brand] = [];
          }
          modelsByBrand[v.brand].push(v.model);
        });
        setModels(modelsByBrand);
      }
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
    }
  };

  const sendConfirmationEmail = async (bookingId) => {
    try {
      const res = await fetch(`${API_URL}/api/garage/bookings/${bookingId}/send-confirmation?garage_id=${garageId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('garage_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Booking confirmed & email sent!');
        fetchBookings();
      } else {
        toast.error(data.message || data.detail || 'Failed to confirm booking');
      }
    } catch (error) {
      console.error('Confirm booking error:', error);
      toast.error('Failed to confirm booking');
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleBooking || !rescheduleDate || !rescheduleTime) {
      toast.error('Please select date and time');
      return;
    }
    
    setRescheduling(true);
    try {
      const res = await fetch(`${API_URL}/api/garage/bookings/${rescheduleBooking.id}/reschedule?garage_id=${garageId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('garage_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          preferred_date: format(rescheduleDate, 'yyyy-MM-dd'),
          preferred_time: rescheduleTime
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Booking rescheduled successfully!');
        setShowRescheduleModal(false);
        setRescheduleBooking(null);
        setRescheduleDate(null);
        setRescheduleTime('');
        fetchBookings();
      } else {
        toast.error(data.message || 'Failed to reschedule');
      }
    } catch (error) {
      toast.error('Failed to reschedule booking');
    } finally {
      setRescheduling(false);
    }
  };

  const openRescheduleModal = (booking) => {
    setRescheduleBooking(booking);
    setRescheduleDate(booking.preferred_date ? new Date(booking.preferred_date) : null);
    setRescheduleTime(booking.preferred_time || '');
    setShowRescheduleModal(true);
  };

  const rescheduleTimeSlots = [
    '9:00 AM - 11:00 AM', '11:00 AM - 1:00 PM', '1:00 PM - 3:00 PM',
    '3:00 PM - 5:00 PM', '5:00 PM - 7:00 PM'
  ];

  // Get available reschedule time slots - requires 2 hour window for same-day
  const getAvailableRescheduleSlots = () => {
    if (!rescheduleDate) return rescheduleTimeSlots;
    
    const now = new Date();
    const isToday = rescheduleDate.toDateString() === now.toDateString();
    
    if (!isToday) return rescheduleTimeSlots;
    
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeIn24 = currentHour + (currentMinute / 60);
    const minBookingTime = currentTimeIn24 + 2;
    
    return rescheduleTimeSlots.filter(slot => {
      const startTime = slot.split(' - ')[0].trim();
      const [time, period] = startTime.split(' ');
      const [hourStr, minuteStr] = time.split(':');
      let hour = parseInt(hourStr);
      
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
      
      const slotStartTime = hour + (parseInt(minuteStr || '0') / 60);
      return slotStartTime >= minBookingTime;
    });
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/garage/bookings/${bookingId}/cancel?garage_id=${garageId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('garage_token')}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Booking cancelled');
        fetchBookings();
      }
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const assignDriver = async (bookingId, driverId) => {
    try {
      const res = await fetch(`${API_URL}/api/garage/bookings/${bookingId}/assign-driver?garage_id=${garageId}&driver_id=${driverId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('garage_token')}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        fetchBookings();
      }
    } catch (error) {
      toast.error('Failed to assign driver');
    }
  };

  const shareBooking = async (booking) => {
    const shareText = `🚗 Booking Details\n\n` +
      `📋 Booking ID: ${booking.booking_ref || booking.id.slice(0, 8).toUpperCase()}\n` +
      `👤 Customer: ${booking.customer_name}\n` +
      `📞 Phone: ${booking.customer_phone}\n` +
      `🚘 Vehicle: ${booking.vehicle_brand} ${booking.vehicle_model} (${booking.vehicle_number})\n` +
      `🔧 Service: ${booking.service_category}\n` +
      `📅 Date: ${booking.preferred_date}\n` +
      `⏰ Time: ${booking.preferred_time}\n` +
      (booking.requires_pickup ? `📍 Pickup: ${booking.address || 'Yes'}\n` : '') +
      (booking.driver_name ? `🚗 Driver: ${booking.driver_name}\n` : '') +
      `\nStatus: ${booking.status?.toUpperCase()}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Booking ${booking.booking_ref || booking.id.slice(0, 8).toUpperCase()}`,
          text: shareText
        });
      } catch (err) {
        await copyToClipboard(shareText);
      }
    } else {
      await copyToClipboard(shareText);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Booking details copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const convertToJob = (booking) => {
    // Navigate to Create Job page with booking data pre-filled
    navigate('/garage/jobs/new', { 
      state: { 
        bookingData: booking 
      } 
    });
  };

  // Apply search filter first
  const searchFilteredBookings = bookings.filter(b => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      b.customer_name?.toLowerCase().includes(query) ||
      b.customer_phone?.includes(query) ||
      b.vehicle_number?.toLowerCase().includes(query) ||
      b.booking_ref?.toLowerCase().includes(query)
    );
  });

  // Apply date filter
  const dateFilteredBookings = searchFilteredBookings.filter(b => {
    if (dateFilter === 'all') return true;
    if (dateFilter === 'today') return isToday(b.preferred_date);
    if (dateFilter === 'tomorrow') return isTomorrow(b.preferred_date);
    if (dateFilter === 'later') return isLater(b.preferred_date);
    return true;
  });

  // Categorize into buckets
  const buckets = categorizeBookings(dateFilteredBookings);
  
  // Get bookings for current bucket
  const filteredBookings = bucketFilter === 'active' ? buckets.active 
    : bucketFilter === 'successful' ? buckets.successful 
    : buckets.missed;

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${theme.text}`}>
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bookings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${theme.text}`}>Bookings</h1>
          <p className={theme.textMuted}>Manage customer bookings and appointments</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> New Booking
        </Button>
      </div>

      {/* Filters */}
      <Card className={`${theme.cardBg} ${theme.border}`}>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Search and Status Filter Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, phone, vehicle, booking ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-10 ${theme.inputBg} ${theme.border}`}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={`w-40 ${theme.inputBg} ${theme.border}`}>
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Date Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              <span className={`text-sm ${theme.textMuted} self-center mr-2`}>Date:</span>
              {[
                { key: 'all', label: 'All Dates' },
                { key: 'today', label: 'Today' },
                { key: 'tomorrow', label: 'Tomorrow' },
                { key: 'later', label: 'Later' }
              ].map(({ key, label }) => (
                <Button
                  key={key}
                  size="sm"
                  variant={dateFilter === key ? 'default' : 'outline'}
                  onClick={() => setDateFilter(key)}
                  className={dateFilter === key ? 'bg-orange-500 hover:bg-orange-600' : theme.border}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bucket Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <Button
          onClick={() => setBucketFilter('active')}
          variant={bucketFilter === 'active' ? 'default' : 'outline'}
          className={`flex-shrink-0 ${bucketFilter === 'active' ? 'bg-blue-500 hover:bg-blue-600' : theme.border}`}
        >
          <Clock className="w-4 h-4 mr-2" />
          Active ({buckets.active.length})
          <span className="ml-2 text-xs opacity-75">Yet to arrive</span>
        </Button>
        <Button
          onClick={() => setBucketFilter('successful')}
          variant={bucketFilter === 'successful' ? 'default' : 'outline'}
          className={`flex-shrink-0 ${bucketFilter === 'successful' ? 'bg-green-500 hover:bg-green-600' : theme.border}`}
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Successful ({buckets.successful.length})
          <span className="ml-2 text-xs opacity-75">Job created</span>
        </Button>
        <Button
          onClick={() => setBucketFilter('missed')}
          variant={bucketFilter === 'missed' ? 'default' : 'outline'}
          className={`flex-shrink-0 ${bucketFilter === 'missed' ? 'bg-red-500 hover:bg-red-600' : theme.border}`}
        >
          <XCircle className="w-4 h-4 mr-2" />
          Missed ({buckets.missed.length})
          <span className="ml-2 text-xs opacity-75">No job card</span>
        </Button>
      </div>

      {/* Stats - Updated to show bucket-relevant stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className={`${theme.cardBg} ${theme.border}`}>
          <CardContent className="p-4 text-center">
            <p className={`text-2xl font-bold ${theme.text}`}>{dateFilteredBookings.length}</p>
            <p className={`text-sm ${theme.textMuted}`}>Total Bookings</p>
          </CardContent>
        </Card>
        <Card className={`${theme.cardBg} ${theme.border} ${bucketFilter === 'active' ? 'ring-2 ring-blue-500' : ''}`}>
          <CardContent className="p-4 text-center cursor-pointer" onClick={() => setBucketFilter('active')}>
            <p className="text-2xl font-bold text-blue-500">{buckets.active.length}</p>
            <p className={`text-sm ${theme.textMuted}`}>Active</p>
          </CardContent>
        </Card>
        <Card className={`${theme.cardBg} ${theme.border} ${bucketFilter === 'successful' ? 'ring-2 ring-green-500' : ''}`}>
          <CardContent className="p-4 text-center cursor-pointer" onClick={() => setBucketFilter('successful')}>
            <p className="text-2xl font-bold text-green-500">{buckets.successful.length}</p>
            <p className={`text-sm ${theme.textMuted}`}>Successful</p>
          </CardContent>
        </Card>
        <Card className={`${theme.cardBg} ${theme.border} ${bucketFilter === 'missed' ? 'ring-2 ring-red-500' : ''}`}>
          <CardContent className="p-4 text-center cursor-pointer" onClick={() => setBucketFilter('missed')}>
            <p className="text-2xl font-bold text-red-500">{buckets.missed.length}</p>
            <p className={`text-sm ${theme.textMuted}`}>Missed</p>
          </CardContent>
        </Card>
      </div>

      {/* Bookings List */}
      <Card className={`${theme.cardBg} ${theme.border}`}>
        <CardHeader className="pb-2">
          <CardTitle className={`text-lg ${theme.text}`}>
            {bucketFilter === 'active' && '🚗 Active Bookings - Vehicles Yet to Arrive'}
            {bucketFilter === 'successful' && '✅ Successful Bookings - Job Cards Created'}
            {bucketFilter === 'missed' && '⚠️ Missed Bookings - No Job Card Created'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredBookings.length === 0 ? (
            <div className="p-8 text-center">
              <CalendarDays className={`w-12 h-12 mx-auto mb-4 ${theme.textMuted}`} />
              <p className={theme.textMuted}>
                {bucketFilter === 'active' && 'No active bookings'}
                {bucketFilter === 'successful' && 'No successful bookings yet'}
                {bucketFilter === 'missed' && 'No missed bookings - Great job!'}
              </p>
              {bucketFilter === 'active' && (
                <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-orange-500 hover:bg-orange-600">
                  <Plus className="w-4 h-4 mr-2" /> Create New Booking
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {filteredBookings.map((booking) => (
                <div key={booking.id} className="p-4 hover:bg-slate-800/50 transition-colors">
                  <div className="flex flex-col gap-3">
                    {/* Header Row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Booking Reference */}
                      <span className="font-mono text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">
                        {booking.booking_ref || `#${booking.id.slice(0, 8).toUpperCase()}`}
                      </span>
                      <h3 className={`font-semibold ${theme.text}`}>{booking.customer_name}</h3>
                      <Badge variant={booking.status === 'confirmed' ? 'default' : 'destructive'} className={booking.status === 'confirmed' ? 'bg-green-600' : ''}>
                        {booking.status}
                      </Badge>
                      {booking.source === 'booking_page' && (
                        <Badge variant="outline" className="text-blue-400 border-blue-400">Online</Badge>
                      )}
                      {booking.email_sent && (
                        <Badge variant="outline" className="text-green-400 border-green-400">Confirmed</Badge>
                      )}
                      {booking.job_id && (
                        <Badge className="bg-purple-600">Job Created</Badge>
                      )}
                      {/* Share Button */}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => shareBooking(booking)}
                        className="ml-auto text-slate-400 hover:text-orange-400"
                        title="Share Booking"
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                      {/* Contact */}
                      <div className={`flex items-start gap-2 ${theme.textMuted}`}>
                        <Phone className="w-4 h-4 mt-0.5 text-orange-500" />
                        <div>
                          <p className={theme.text}>{booking.customer_phone}</p>
                          {booking.customer_email && <p className="text-xs">{booking.customer_email}</p>}
                        </div>
                      </div>
                      
                      {/* Vehicle */}
                      <div className={`flex items-start gap-2 ${theme.textMuted}`}>
                        <Car className="w-4 h-4 mt-0.5 text-orange-500" />
                        <div>
                          <p className={theme.text}>{booking.vehicle_brand} {booking.vehicle_model}</p>
                          <p className="text-xs">{booking.vehicle_number}</p>
                        </div>
                      </div>
                      
                      {/* Service */}
                      <div className={`flex items-start gap-2 ${theme.textMuted}`}>
                        <Clock className="w-4 h-4 mt-0.5 text-orange-500" />
                        <div>
                          <p className={`${theme.text} text-orange-400`}>{booking.service_category}</p>
                        </div>
                      </div>
                      
                      {/* Schedule */}
                      <div className={`flex items-start gap-2 ${theme.textMuted}`}>
                        <CalendarIcon className="w-4 h-4 mt-0.5 text-blue-500" />
                        <div>
                          <p className={theme.text}>{booking.preferred_date}</p>
                          <p className="text-xs text-blue-400">{booking.preferred_time}</p>
                        </div>
                      </div>
                      
                      {/* Pickup Address */}
                      {booking.requires_pickup && (
                        <div className={`flex items-start gap-2 ${theme.textMuted}`}>
                          <MapPin className="w-4 h-4 mt-0.5 text-green-500" />
                          <div>
                            <p className={theme.text}>{booking.address || 'Address not provided'}</p>
                            <p className="text-xs text-green-400">
                              Driver: {booking.driver_name || 'Not assigned'}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Complaint/Notes */}
                      {booking.customer_complaint && (
                        <div className={`flex items-start gap-2 ${theme.textMuted} md:col-span-2 lg:col-span-1`}>
                          <Edit3 className="w-4 h-4 mt-0.5 text-yellow-500" />
                          <p className="text-xs italic">{booking.customer_complaint}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-700">
                      {booking.status === 'confirmed' && (
                        <>
                          {!booking.email_sent && booking.customer_email && (
                            <Button size="sm" onClick={() => sendConfirmationEmail(booking.id)} className="bg-green-600 hover:bg-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" /> Confirm Booking
                            </Button>
                          )}
                          
                          <Button size="sm" variant="outline" onClick={() => openRescheduleModal(booking)} className={theme.border}>
                            <RefreshCw className="w-3 h-3 mr-1" /> Reschedule
                          </Button>
                          
                          {booking.requires_pickup && !booking.driver_id && drivers.length > 0 && (
                            <Select onValueChange={(driverId) => assignDriver(booking.id, driverId)}>
                              <SelectTrigger className="w-36 h-8 text-xs">
                                <Truck className="w-3 h-3 mr-1" /> Assign Driver
                              </SelectTrigger>
                              <SelectContent>
                                {drivers.map(d => (
                                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          
                          {!booking.job_id && (
                            <Button size="sm" onClick={() => convertToJob(booking)} variant="outline" className={theme.border} data-testid={`convert-job-${booking.id}`}>
                              <ArrowRight className="w-3 h-3 mr-1" /> Convert to Job
                            </Button>
                          )}
                          
                          <Button size="sm" variant="destructive" onClick={() => cancelBooking(booking.id)}>
                            <XCircle className="w-3 h-3 mr-1" /> Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Booking Modal */}
      <CreateBookingModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        garageId={garageId}
        onBookingCreated={(booking) => setBookings(prev => [booking, ...prev])}
        brands={brands}
        models={models}
        fuelTypes={fuelTypes}
      />

      {/* Reschedule Modal */}
      <Dialog open={showRescheduleModal} onOpenChange={setShowRescheduleModal}>
        <DialogContent className={`max-w-md ${theme.cardBg} ${theme.border}`}>
          <DialogHeader>
            <DialogTitle className={`${theme.text} flex items-center gap-2`}>
              <RefreshCw className="w-5 h-5 text-orange-500" />
              Reschedule Booking
            </DialogTitle>
          </DialogHeader>
          
          {rescheduleBooking && (
            <div className="space-y-4">
              <div className={`p-3 rounded-lg ${theme.cardBgSolid} border ${theme.border}`}>
                <p className={`font-semibold ${theme.text}`}>{rescheduleBooking.customer_name}</p>
                <p className={`text-sm ${theme.textMuted}`}>{rescheduleBooking.service_category}</p>
                <p className={`text-sm ${theme.textMuted}`}>
                  Current: {rescheduleBooking.preferred_date} @ {rescheduleBooking.preferred_time}
                </p>
              </div>
              
              <div>
                <Label className={theme.text}>New Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={`w-full justify-start text-left ${theme.inputBg} ${theme.border}`}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {rescheduleDate ? format(rescheduleDate, 'PPP') : 'Select new date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={rescheduleDate}
                      onSelect={(date) => {
                        setRescheduleDate(date);
                        setRescheduleTime(''); // Clear time when date changes
                      }}
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <Label className={theme.text}>New Time Slot *</Label>
                <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                  <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                    <SelectValue placeholder="Select time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableRescheduleSlots().length > 0 ? (
                      getAvailableRescheduleSlots().map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)
                    ) : (
                      <SelectItem value="none" disabled>No slots available today</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {rescheduleDate && rescheduleDate.toDateString() === new Date().toDateString() && getAvailableRescheduleSlots().length === 0 && (
                  <p className="text-xs text-orange-500 mt-1">Same-day reschedule requires 2 hours notice. Please select tomorrow.</p>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRescheduleModal(false)} className={theme.border}>
                  Cancel
                </Button>
                <Button onClick={handleReschedule} disabled={rescheduling} className="bg-orange-500 hover:bg-orange-600">
                  {rescheduling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Reschedule
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bookings;
